import type { DocumentData } from "firebase/firestore";
import type { LeagueModel } from "./catalog";
import { dateValue, stringValue } from "./firestore";
import { aiUsageLimitFirestoreData, parseAIUsageLimit, type AIUsageLimit } from "./aiUsageLimits";

export const LEAGUE_BILLING_TIERS = ["free", "paid", "internal"] as const;
export const LEAGUE_BILLING_SCHEDULES = ["weekly", "monthly", "yearly"] as const;
export const LEAGUE_BILLING_REMINDER_LEADS = [0, 2, 3, 5, 7] as const;
export const MAX_INVOICE_AMOUNT_USD = 1_000_000;
export const MAX_INVOICE_MESSAGE_LENGTH = 2_000;
export const MAX_BILLING_OCCURRENCE_SCAN = 520;

export type LeagueBillingTier = (typeof LEAGUE_BILLING_TIERS)[number];
export type LeagueBillingSchedule = (typeof LEAGUE_BILLING_SCHEDULES)[number];
export type LeagueBillingReminderLead = (typeof LEAGUE_BILLING_REMINDER_LEADS)[number];

export interface LeagueBillingInvoice {
  amountUSD: number;
  message: string;
  schedule: LeagueBillingSchedule;
  startAt: Date;
  endAt?: Date;
  remindDaysBefore: LeagueBillingReminderLead;
}

export interface LeagueBilling {
  id: string;
  leagueID: string;
  leagueName: string;
  tier: LeagueBillingTier;
  invoice: LeagueBillingInvoice | null;
  aiUsage: AIUsageLimit | null;
  updatedAt?: Date;
  updatedByUserID?: string;
  /** Scheduler-owned, always read-only on the client. */
  nextDueAt?: Date;
  advanceReminderSentForDueAt?: Date;
  dueEmailSentForDueAt?: Date;
  lastSweepAt?: Date;
}

export interface LeagueBillingAdminEntry {
  league: LeagueModel;
  billing: LeagueBilling;
  hasStoredBilling: boolean;
}

export class LeagueBillingInputError extends Error {
  readonly code:
    | "notAuthorized"
    | "missingInvoice"
    | "invalidAmount"
    | "amountTooLarge"
    | "messageTooLong"
    | "endBeforeStart";

  constructor(code: LeagueBillingInputError["code"], message: string) {
    super(message);
    this.name = "LeagueBillingInputError";
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function billingTier(value: unknown): LeagueBillingTier {
  return LEAGUE_BILLING_TIERS.includes(value as LeagueBillingTier) ? value as LeagueBillingTier : "free";
}

function billingSchedule(value: unknown): LeagueBillingSchedule {
  return LEAGUE_BILLING_SCHEDULES.includes(value as LeagueBillingSchedule)
    ? value as LeagueBillingSchedule
    : "monthly";
}

function reminderLead(value: unknown): LeagueBillingReminderLead {
  return LEAGUE_BILLING_REMINDER_LEADS.includes(value as LeagueBillingReminderLead)
    ? value as LeagueBillingReminderLead
    : 3;
}

export function parseLeagueBillingInvoice(value: unknown): LeagueBillingInvoice | null {
  if (!isRecord(value) || typeof value.amountUSD !== "number" || !Number.isFinite(value.amountUSD)) return null;
  const startAt = dateValue(value.startAt);
  if (!startAt) return null;
  return {
    amountUSD: value.amountUSD,
    message: typeof value.message === "string" ? value.message : "",
    schedule: billingSchedule(value.schedule),
    startAt,
    ...(dateValue(value.endAt) ? { endAt: dateValue(value.endAt) } : {}),
    remindDaysBefore: reminderLead(value.remindDaysBefore),
  };
}

/** Requires the stored `id` to match the Firestore document ID. */
export function parseLeagueBilling(id: string, data: DocumentData): LeagueBilling | null {
  if (typeof data.id !== "string" || !data.id.trim() || data.id !== id) return null;
  if (typeof data.leagueID === "string" && data.leagueID && data.leagueID !== id) return null;
  return {
    id,
    leagueID: typeof data.leagueID === "string" ? data.leagueID : id,
    leagueName: stringValue(data.leagueName) ?? id,
    tier: billingTier(data.tier),
    invoice: parseLeagueBillingInvoice(data.invoice),
    aiUsage: parseAIUsageLimit(data.aiUsage),
    updatedAt: dateValue(data.updatedAt),
    updatedByUserID: stringValue(data.updatedByUserID),
    nextDueAt: dateValue(data.nextDueAt),
    advanceReminderSentForDueAt: dateValue(data.advanceReminderSentForDueAt),
    dueEmailSentForDueAt: dateValue(data.dueEmailSentForDueAt),
    lastSweepAt: dateValue(data.lastSweepAt),
  };
}

export function defaultLeagueBilling(leagueID: string, leagueName: string): LeagueBilling {
  return { id: leagueID, leagueID, leagueName, tier: "free", invoice: null, aiUsage: null };
}

export function leagueBillingInvoiceFirestoreData(invoice: LeagueBillingInvoice): Record<string, unknown> {
  return {
    amountUSD: invoice.amountUSD,
    message: invoice.message,
    schedule: invoice.schedule,
    startAt: invoice.startAt,
    endAt: invoice.endAt ?? null,
    remindDaysBefore: invoice.remindDaysBefore,
  };
}

/** Billing editor payload. Deliberately excludes aiUsage and all scheduler-owned fields. */
export function leagueBillingClientWritePayload(billing: LeagueBilling): Record<string, unknown> {
  return {
    id: billing.id,
    leagueID: billing.leagueID,
    leagueName: billing.leagueName,
    tier: billing.tier,
    invoice: billing.invoice ? leagueBillingInvoiceFirestoreData(billing.invoice) : null,
    updatedAt: billing.updatedAt ?? null,
    updatedByUserID: billing.updatedByUserID ?? null,
  };
}

/** Negotiated-limit editor payload. Deliberately excludes billing and scheduler fields. */
export function leagueAIUsageClientWritePayload(
  limit: AIUsageLimit | null,
  updatedAt: Date,
  updatedByUserID: string,
): Record<string, unknown> {
  return {
    aiUsage: limit ? aiUsageLimitFirestoreData(limit) : null,
    updatedAt,
    updatedByUserID,
  };
}

/** Full snapshot for audit diffing only; never pass this object to setDoc. */
export function leagueBillingFirestoreData(billing: LeagueBilling): Record<string, unknown> {
  return {
    ...leagueBillingClientWritePayload(billing),
    aiUsage: billing.aiUsage ? aiUsageLimitFirestoreData(billing.aiUsage) : null,
    nextDueAt: billing.nextDueAt ?? null,
    advanceReminderSentForDueAt: billing.advanceReminderSentForDueAt ?? null,
    dueEmailSentForDueAt: billing.dueEmailSentForDueAt ?? null,
    lastSweepAt: billing.lastSweepAt ?? null,
  };
}

export function normalizeLeagueBillingDraft(
  draft: LeagueBilling,
  original: LeagueBilling,
  isSBotLabsAdmin: boolean,
  now = new Date(),
): LeagueBilling {
  if (!isSBotLabsAdmin) {
    throw new LeagueBillingInputError("notAuthorized", "You must be an SBot Labs admin to change league billing.");
  }

  let invoice = draft.invoice;
  if (draft.tier === "paid") {
    if (!invoice) {
      throw new LeagueBillingInputError("missingInvoice", "Paid Tier requires an invoice amount and schedule.");
    }
    if (!Number.isFinite(invoice.amountUSD) || invoice.amountUSD <= 0) {
      throw new LeagueBillingInputError("invalidAmount", "Enter an invoice amount greater than zero.");
    }
    if (invoice.amountUSD > MAX_INVOICE_AMOUNT_USD) {
      throw new LeagueBillingInputError("amountTooLarge", "The invoice amount must be $1,000,000 or less.");
    }
    const message = invoice.message.trim();
    if (message.length > MAX_INVOICE_MESSAGE_LENGTH) {
      throw new LeagueBillingInputError("messageTooLong", "The invoice message must be 2,000 characters or fewer.");
    }
    if (invoice.endAt && invoice.endAt.getTime() < invoice.startAt.getTime()) {
      throw new LeagueBillingInputError("endBeforeStart", "The invoice end date must be on or after the start date.");
    }
    invoice = { ...invoice, amountUSD: Math.round(invoice.amountUSD * 100) / 100, message };
  } else if (!invoice && original.invoice) {
    // Toggling tiers must not silently destroy a configured invoice.
    invoice = original.invoice;
  }

  return {
    ...draft,
    id: original.id,
    leagueID: original.leagueID,
    leagueName: draft.leagueName.trim() || original.leagueName,
    invoice,
    aiUsage: original.aiUsage,
    updatedAt: now,
    nextDueAt: original.nextDueAt,
    advanceReminderSentForDueAt: original.advanceReminderSentForDueAt,
    dueEmailSentForDueAt: original.dueEmailSentForDueAt,
    lastSweepAt: original.lastSweepAt,
  };
}

export function daysInUTCMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

export function billingOccurrenceUTC(
  schedule: LeagueBillingSchedule,
  startAt: Date,
  index: number,
): Date {
  if (schedule === "weekly") return new Date(startAt.getTime() + index * 7 * 86_400_000);
  const monthsToAdd = schedule === "monthly" ? index : index * 12;
  const totalMonths = startAt.getUTCMonth() + monthsToAdd;
  const year = startAt.getUTCFullYear() + Math.floor(totalMonths / 12);
  const month = ((totalMonths % 12) + 12) % 12;
  const day = Math.min(startAt.getUTCDate(), daysInUTCMonth(year, month));
  return new Date(Date.UTC(
    year,
    month,
    day,
    startAt.getUTCHours(),
    startAt.getUTCMinutes(),
    startAt.getUTCSeconds(),
    startAt.getUTCMilliseconds(),
  ));
}

export function nextBillingOccurrenceUTC(
  invoice: Pick<LeagueBillingInvoice, "schedule" | "startAt" | "endAt">,
  instant: Date,
): Date | null {
  for (let index = 0; index <= MAX_BILLING_OCCURRENCE_SCAN; index += 1) {
    const candidate = billingOccurrenceUTC(invoice.schedule, invoice.startAt, index);
    if (invoice.endAt && candidate.getTime() > invoice.endAt.getTime()) return null;
    if (candidate.getTime() >= instant.getTime()) return candidate;
  }
  return null;
}
